class SidebarMenu extends HTMLElement {
    constructor() {
        super();
        this._boundEvents = {};
    }

    connectedCallback() {
        this.openButton = document.querySelector('.sidebar_open_button');
        this.closeButton = this.querySelector('.sidebar-close');
        this.accordions = [...this.querySelectorAll('.sidebar_accordion')];

        if (!this.openButton || !this.closeButton) {
            console.warn('SidebarMenu: Missing required elements');
            return;
        }

        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('inert', '');

        this._bindEvent(this.openButton, 'click', this.toggleSidebar.bind(this));
        this._bindEvent(this.closeButton, 'click', this.closeSidebar.bind(this));
        this._bindEvent(document, 'keydown', this._handleKeydown.bind(this));

        this._initAccordions();
    }

    disconnectedCallback() {
        Object.values(this._boundEvents).forEach(({ target, type, handler }) => {
            target.removeEventListener(type, handler);
        });
    }

    // --- Sidebar Control ---

    toggleSidebar() {
        const isOpen = this.classList.toggle('open');
        this._updateSidebarState(isOpen);
    }

    closeSidebar() {
        if (!this.classList.contains('open')) return;
        this.classList.remove('open');
        this._updateSidebarState(false);
    }

    _updateSidebarState(isOpen) {
        document.body.classList.toggle('overflow-hidden', isOpen);
        this.setAttribute('aria-hidden', (!isOpen).toString());
        this.toggleAttribute('inert', !isOpen);
        this.openButton.setAttribute('aria-expanded', isOpen.toString());
        this.openButton.querySelector('.hamburger')?.classList.toggle('open', isOpen);
        this.closeButton.querySelector('.hamburger')?.classList.toggle('open', isOpen);

        if (isOpen) {
            setTimeout(() => trapFocus(this, this.closeButton), 110);
        } else {
            setTimeout(() => this._closeAllAccordions(), 200);
            setTimeout(() => {
                this.accordions.length && this.accordions[0].classList.add('open');
                if (this.accordions.length) {
                    const firstButton = this.accordions[0].querySelector('.sidebar_accordion_title');
                    const firstContent = this.accordions[0].querySelector('.sidebar_accordion_content');
                    this._setupAccordion(firstButton, firstContent, true);
                }
                this.openButton.focus();
            }, 201);
            
        }
    }

    _handleKeydown(e) {
        if (e.key === 'Escape') {
            this.closeSidebar();
        }
    }

    // --- Accordion ---

    _initAccordions() {
        this.accordions.forEach(accordion => {
            const button = accordion.querySelector('.sidebar_accordion_title');
            const content = accordion.querySelector('.sidebar_accordion_content');
            const isOpen = accordion.classList.contains('open');

            this._setupAccordion(button, content, isOpen);

            button.addEventListener('click', () => {
                this.accordions.forEach(otherAccordion => {
                    if (otherAccordion !== accordion && otherAccordion.classList.contains('open')) {
                        const otherButton = otherAccordion.querySelector('.sidebar_accordion_title');
                        const otherContent = otherAccordion.querySelector('.sidebar_accordion_content');
                        otherAccordion.classList.remove('open');
                        this._setupAccordion(otherButton, otherContent, false);
                    }
                });
                const nowOpen = accordion.classList.toggle('open');
                this._setupAccordion(button, content, nowOpen);
            });

            button.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    button.click();
                }
            });
        });
    }

    _setupAccordion(button, content, isOpen) {
        button.setAttribute('aria-expanded', isOpen.toString());
        content.setAttribute('aria-hidden', (!isOpen).toString());
        content.style.maxHeight = isOpen ? `${content.scrollHeight}px` : '0';
    }

    _closeAllAccordions() {
        this.accordions.forEach(accordion => {
            if (accordion.classList.contains('open')) {
                const button = accordion.querySelector('.sidebar_accordion_title');
                const content = accordion.querySelector('.sidebar_accordion_content');
                accordion.classList.remove('open');
                this._setupAccordion(button, content, false);
            }
        });
    }

    // --- Utility ---

    _bindEvent(target, type, handler) {
        target.addEventListener(type, handler);
        this._boundEvents[`${type}_${target.className || target.tagName}`] = { target, type, handler };
    }
}
customElements.define('sidebar-menu', SidebarMenu);
